// Share modal — QR (dynamic-imported qrcode) + copy link + privacy notice.
// Privacy-first: harus dipanggil SETELAH confirm opt-in di parent (lihat
// App.js handleShare). Modal ini TIDAK menampilkan confirm sendiri.
//
// Lifecycle:
//   const { close, url } = await mountShareModal({ project })
//   // ... user closes via × / backdrop / ESC
//   close() // explicit teardown
//
// Dependencies yang di-load lazy:
//   - qrcode (~15-20 KB gz) via await import('qrcode')
//   - clipboard via navigator.clipboard.writeText (with execCommand fallback)

import { STRINGS } from '@tm/shared'
import { showToast } from '@tm/ui-kit'
import { encodeProjectToUrl, estimateUrlSize, SHARE_URL_MAX_BYTES } from './encode.js'

let escHandler = null

function detachEscHandler() {
  if (escHandler) {
    document.removeEventListener('keydown', escHandler)
    escHandler = null
  }
}

function buildTooLongModal({ project, onClose }) {
  const overlay = document.createElement('div')
  overlay.className = 'tm-share-modal__backdrop'
  overlay.setAttribute('role', 'dialog')
  overlay.setAttribute('aria-modal', 'true')
  overlay.setAttribute('aria-label', STRINGS.shareModalTitle)

  const dialog = document.createElement('div')
  dialog.className = 'tm-share-modal'
  // Defensive: detach any zombie ESC handler from a previous mount.
  detachEscHandler()

  const sizeKb = Math.round(estimateUrlSize(project) / 1024)
  const maxKb = Math.round(SHARE_URL_MAX_BYTES / 1024)

  dialog.innerHTML = `
    <button class="tm-share-modal__close" type="button" aria-label="Tutup">×</button>
    <h2 class="tm-share-modal__title">${STRINGS.shareModalTitle}</h2>
    <p class="tm-share-modal__body tm-share-modal__error">
      ${STRINGS.shareUrlTooLong}
      <br />
      <small>(${sizeKb} KB / max ${maxKb} KB)</small>
    </p>
    <div class="tm-share-modal__actions">
      <button class="tm-share-modal__btn tm-share-modal__btn-primary" data-tm-action="ok">${STRINGS.close}</button>
    </div>
  `
  overlay.appendChild(dialog)
  document.body.appendChild(overlay)

  function close() {
    overlay.remove()
    detachEscHandler()
    if (typeof onClose === 'function') onClose()
  }

  dialog.querySelector('[data-tm-action="ok"]').addEventListener('click', close)
  dialog.querySelector('.tm-share-modal__close').addEventListener('click', close)
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close()
  })
  escHandler = (e) => {
    if (e.key === 'Escape') close()
  }
  document.addEventListener('keydown', escHandler)

  return { close, error: new Error('URL_TOO_LONG') }
}

export async function mountShareModal({ project, onClose } = {}) {
  if (!project || typeof project !== 'object') {
    throw new Error('mountShareModal: project is required')
  }

  // Defensive: pastikan ESC handler dari mount sebelumnya sudah dilepas
  // agar tidak ada listener zombie yang menahan modal ini.
  detachEscHandler()

  let url
  try {
    url = encodeProjectToUrl(project)
  } catch (err) {
    if (err.code === 'URL_TOO_LONG' || err.message === 'URL_TOO_LONG') {
      const tooLong = buildTooLongModal({ project, onClose })
      return { close: tooLong.close, url: null, error: err }
    }
    throw err
  }

  const overlay = document.createElement('div')
  overlay.className = 'tm-share-modal__backdrop'
  overlay.setAttribute('role', 'dialog')
  overlay.setAttribute('aria-modal', 'true')
  overlay.setAttribute('aria-label', STRINGS.shareModalTitle)

  const dialog = document.createElement('div')
  dialog.className = 'tm-share-modal'

  // Project name = public; escape before injecting into innerHTML.
  const hintText = escapeHtml(
    STRINGS.shareProjectNameHint.replace('%s', String(project.name || 'Anonim')),
  )

  dialog.innerHTML = `
    <button class="tm-share-modal__close" type="button" aria-label="Tutup">×</button>
    <h2 class="tm-share-modal__title">${STRINGS.shareModalTitle}</h2>
    <p class="tm-share-modal__body">${STRINGS.shareModalDesc}</p>
    <div class="tm-share-modal__content">
      <div class="tm-share-modal__qr" data-tm-qr-host aria-label="QR code"></div>
      <div class="tm-share-modal__actions">
        <p class="tm-share-modal__hint">${hintText}</p>
        <input class="tm-share-modal__url" readonly value="" data-tm-url-input />
        <button class="tm-share-modal__btn tm-share-modal__btn-primary" type="button" data-tm-action="copy">${STRINGS.shareCopyLink}</button>
        <button class="tm-share-modal__btn tm-share-modal__btn-secondary" type="button" data-tm-action="screenshot" disabled>${STRINGS.shareScreenshot} <small>(${STRINGS.shareScreenshotSoon})</small></button>
      </div>
    </div>
    <p class="tm-share-modal__privacy">${STRINGS.sharePrivacyNotice}</p>
  `

  // Set URL value via property (not attribute) so it avoids HTML escaping.
  const urlInput = dialog.querySelector('[data-tm-url-input]')
  if (urlInput) urlInput.value = url

  overlay.appendChild(dialog)
  document.body.appendChild(overlay)

  // Lazy-load qrcode (~15-20 KB gz) inside its own chunk.
  try {
    const QRCode = (await import('qrcode')).default
    const qrHost = dialog.querySelector('[data-tm-qr-host]')
    const canvas = document.createElement('canvas')
    qrHost.appendChild(canvas)
    await QRCode.toCanvas(canvas, url, {
      width: 180,
      margin: 1,
      color: { dark: '#1a1a1a', light: '#ffffff' },
    })
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[ShareModal] QR render failed:', err)
    const qrHost = dialog.querySelector('[data-tm-qr-host]')
    qrHost.innerHTML = `<p class="tm-share-modal__error">${escapeHtml(STRINGS.shareQrFailed)}</p>`
  }

  function close() {
    overlay.remove()
    detachEscHandler()
    if (typeof onClose === 'function') onClose()
  }

  // Copy link button.
  const copyBtn = dialog.querySelector('[data-tm-action="copy"]')
  copyBtn.addEventListener('click', async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url)
      } else {
        // Fallback: select input + execCommand('copy').
        const input = dialog.querySelector('[data-tm-url-input]')
        if (input) {
          input.select()
          document.execCommand?.('copy')
        }
      }
      showToast(STRINGS.shareCopied, { type: 'success', durationMs: 1500 })
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[ShareModal] copy failed:', err)
      const input = dialog.querySelector('[data-tm-url-input]')
      if (input) input.select()
      showToast(STRINGS.shareCopyFailed, { type: 'warn', durationMs: 2500 })
    }
  })

  // Close affordances.
  dialog.querySelector('.tm-share-modal__close').addEventListener('click', close)
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close()
  })
  escHandler = (e) => {
    if (e.key === 'Escape') close()
  }
  document.addEventListener('keydown', escHandler)

  return { close, url }
}

function escapeHtml(str) {
  const div = document.createElement('div')
  div.textContent = String(str)
  return div.innerHTML
}
